'use client';

import React, { useEffect, useRef, useMemo } from 'react';
import Link from 'next/link';
import { useQuery } from '@apollo/client';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { useCart } from '@/contexts/CartContext';
import { useWishlist } from '@/contexts/WishlistContext';
import { GET_FEATURED_PRODUCTS, GET_PRODUCTS } from '@/lib/graphql-queries';
import { transformGraphQLProducts } from '@/utils/transformGraphQLProduct';

const FALLBACK_IMAGE = '/assets/images/kym-products/kym-jar-07.jpg';

function parsePrice(raw: string | number | null | undefined): number {
  if (raw === null || raw === undefined) return 0;
  if (typeof raw === 'number') return raw;
  const match = String(raw).match(/[\d.,]+/);
  if (!match) return 0;
  return parseFloat(match[0].replace(/,/g, '')) || 0;
}

// Image src extraction — beauty-store's getImageSrc pattern. The transform
// utility SOMETIMES produces `{src}` and sometimes leaves `{sourceUrl}` on
// the images array (Apollo cache state dependent). The ProductCard in
// beauty-store checks both explicitly because the inconsistency is the
// week-long bug that cost real debugging time. Categories have the same
// pattern — transform may leave them as `productCategories.nodes[]` raw.
function getImageSrc(product: any, fallback: string): string {
  // Transformed shape: images[0].src / .sourceUrl
  const img0 = product?.images?.[0];
  if (img0?.src) return img0.src;
  if (img0?.sourceUrl) return img0.sourceUrl;
  // Raw GraphQL shape: image.sourceUrl / image.src
  if (product?.image?.sourceUrl) return product.image.sourceUrl;
  if (product?.image?.src) return product.image.src;
  return fallback;
}

function getImageAlt(product: any): string {
  const img0 = product?.images?.[0];
  return img0?.alt || img0?.altText || product?.image?.altText || product?.image?.alt || product?.name || 'SweetSkyn product';
}

function getCategoryFromProduct(product: any): { name: string; slug: string } {
  // Transformed shape: categories[]
  const cat = product?.categories?.[0];
  if (cat?.name) return { name: cat.name, slug: cat.slug || '' };
  // Raw GraphQL shape: productCategories.nodes[]
  const rawCat = product?.productCategories?.nodes?.[0];
  if (rawCat?.name) return { name: rawCat.name, slug: rawCat.slug || '' };
  return { name: 'Body Butter', slug: '' };
}

function toFeaturedProduct(wcProduct: any) {
  const cat = getCategoryFromProduct(wcProduct);
  const priceRaw = wcProduct?.price || wcProduct?.regular_price || wcProduct?.regularPrice || '0';
  return {
    id: String(wcProduct?.databaseId || wcProduct?.id || wcProduct?.productId || wcProduct?.slug || ''),
    slug: wcProduct?.slug || '',
    name: (wcProduct?.name || '').replace(/^Sweetskyn Body Butter\s*[—-]\s*/i, ''),
    brand: 'SweetSkyn by Kym',
    price: parsePrice(priceRaw),
    originalPrice: wcProduct?.on_sale || wcProduct?.onSale ? parsePrice(wcProduct?.regular_price || wcProduct?.regularPrice) : null,
    rating: parseFloat(wcProduct?.average_rating || wcProduct?.averageRating || '0') || 0,
    reviews: wcProduct?.rating_count || wcProduct?.reviewCount || 0,
    category: cat.name,
    categorySlug: cat.slug,
    badge: wcProduct?.featured ? 'Featured' : null,
    image: getImageSrc(wcProduct, FALLBACK_IMAGE),
    alt: getImageAlt(wcProduct),
    href: `/product-detail/${encodeURIComponent(wcProduct?.slug || '')}`,
  };
}


function StarRating({ rating }: {rating: number;}) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) =>
      <svg key={star} className={`w-3 h-3 ${star <= Math.round(rating) ? 'text-accent' : 'text-muted'} fill-current`} viewBox="0 0 20 20">
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
        </svg>
      )}
    </div>);

}

export default function FeaturedProductsSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const { addToCart } = useCart();
  const { addToWishlist, isInWishlist } = useWishlist();

  // Try featured-only first; fall back to latest products ONLY when featured
  // returns empty. Running the fallback unconditionally collides with
  // ProductsClient's GET_PRODUCTS cache and overwrites it with 4 products.
  const { data: featuredData, loading: featuredLoading } = useQuery(GET_FEATURED_PRODUCTS, {
    variables: { first: 4 },
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });
  const featuredCount = featuredData?.products?.nodes?.length ?? 0;
  const shouldFallback = !featuredLoading && featuredCount === 0;
  const { data: fallbackData } = useQuery(GET_PRODUCTS, {
    variables: { first: 4 },
    skip: !shouldFallback,
    errorPolicy: 'all',
    fetchPolicy: 'no-cache',
  });

  const featuredProducts = useMemo(() => {
    const featuredNodes = featuredData?.products?.nodes || [];
    const fallbackNodes = fallbackData?.products?.nodes || [];
    const picked = featuredNodes.length >= 1 ? featuredNodes : fallbackNodes;
    const wc = transformGraphQLProducts(picked).slice(0, 4);
    return wc.map(toFeaturedProduct);
  }, [featuredData, fallbackData]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-hidden').forEach((el, i) => {
              setTimeout(() => el.classList.remove('reveal-hidden'), i * 100);
            });
          }
        });
      },
      { threshold: 0.08 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // When Apollo data arrives AFTER the IntersectionObserver has already fired
  // (user scrolled past the section while the query was still in flight),
  // the newly-rendered product cards have `.reveal-hidden` but no observer
  // callback runs to strip it — they stay invisible forever. Strip the
  // class ourselves once data is present.
  useEffect(() => {
    if (!sectionRef.current || featuredProducts.length === 0) return;
    const hidden = sectionRef.current.querySelectorAll('.reveal-hidden');
    hidden.forEach((el, i) => {
      setTimeout(() => el.classList.remove('reveal-hidden'), i * 80);
    });
  }, [featuredProducts.length]);

  return (
    <section ref={sectionRef} className="py-16 md:py-24 px-6 md:px-10 max-w-7xl mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
        <div className="reveal reveal-hidden">
          <span className="section-label block mb-3">Curated for You</span>
          <h2 className="font-display text-4xl md:text-5xl font-light tracking-tight text-foreground">
            Featured products
          </h2>
        </div>
        <Link
          href="/products"
          className="reveal reveal-hidden text-[11px] font-semibold uppercase tracking-widest text-foreground border-b border-foreground pb-0.5 hover:text-accent hover:border-accent transition-colors self-start md:self-auto">
          
          View All →
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
        {featuredProducts.map((product, idx) =>
        <div
          key={product.id}
          className="reveal reveal-hidden product-card group"
          style={{ transitionDelay: `${idx * 80}ms` }}>
          
            <Link href={product.href}>
              <div className="relative overflow-hidden card-rounded bg-secondary aspect-[3/4]">
                <AppImage
                src={product.image}
                alt={product.alt}
                fill
                sizes="(max-width: 640px) 50vw, (max-width: 1024px) 50vw, 25vw"
                className="object-cover product-card-img" />
              
                {product.badge &&
              <span className="absolute top-3 left-3 px-2.5 py-1 bg-primary text-primary-foreground text-[9px] font-semibold uppercase tracking-wider">
                    {product.badge}
                  </span>
              }
                {/* Hover overlay */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-500" />
                {/* Quick add */}
                <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-spring">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      addToCart({
                        id: `ssk-bb-${product.id}-4oz`,
                        product_id: Number(product.id) || 0,
                        name: `${product.name} · 4 oz`,
                        price: String(product.price),
                        regular_price: String(product.price),
                        sale_price: '',
                        on_sale: false,
                        images: [{ id: 0, src: product.image, name: product.name, alt: product.alt } as any],
                        categories: [{ id: 0, name: product.category, slug: 'natural-body-butters' } as any],
                        sku: `SSK-BB-${product.id}-4OZ`,
                        meta_data: [
                          { key: 'scent', value: product.name },
                          { key: 'size', value: '4 oz' },
                        ],
                      } as any, 1);
                    }}
                    className="w-full bg-white text-primary text-[10px] font-semibold uppercase tracking-widest py-3 hover:bg-accent hover:text-accent-foreground transition-colors">
                    Quick Add
                  </button>
                </div>
                {/* Wishlist */}
                <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  addToWishlist({
                    id: Number(product.id),
                    name: product.name,
                    price: String(product.price),
                    regular_price: String(product.price),
                    images: [{ id: 0, src: product.image, name: product.name, alt: product.alt } as any],
                    categories: [{ id: 0, name: product.category, slug: 'natural-body-butters' } as any],
                    sku: `SSK-BB-${product.id}`,
                  } as any);
                }}
                className={`absolute top-3 right-3 p-2 bg-white/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-accent hover:text-white ${isInWishlist(Number(product.id)) ? 'text-accent' : 'text-primary'}`}
                aria-label="Add to wishlist">

                  <Icon name="HeartIcon" size={14} />
                </button>
              </div>

              <div className="mt-4 space-y-1.5">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{product.category}</span>
                <h3 className="font-medium text-sm text-foreground leading-snug">{product.name}</h3>
                <div className="flex items-center gap-2">
                  <StarRating rating={product.rating} />
                  <span className="text-[10px] text-muted-foreground">({product.reviews})</span>
                </div>
                <div className="flex items-center gap-2 pt-0.5">
                  <span className="text-sm font-semibold text-foreground"><span className="font-sans">J$</span>{product.price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  {product.originalPrice &&
                <span className="text-xs text-muted-foreground line-through"><span className="font-sans">J$</span>{product.originalPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                }
                </div>
              </div>
            </Link>
          </div>
        )}
      </div>
    </section>);

}